<template>
    <div
        class="fixed top-16 hidden h-[calc(100vh-4rem)] shrink-0 flex-col overflow-y-hidden bg-white pt-5 md:flex transition-[width] duration-200"
        :class="{
            'z-30': opened,
            'border-r border-gray-200': !opened,
            'w-16': !opened,
            'w-[250px]': opened,
            'xl:w-[250px]': true,
        }"
    >
        <transition
            enter-active-class="transition duration-200 ease-out transform"
            enter-from-class="-translate-x-10 opacity-0 "
            enter-to-class="translate-x-0 opacity-100 "
            leave-active-class="transition duration-100 ease-in transform"
            leave-from-class="translate-x-0 opacity-100"
            leave-to-class="-translate-x-10 opacity-0"
        >
            <div
                v-if="opened"
                class="hidden xl:hidden"
                :class="{
                    'absolute left-[63px] top-0 z-[-2] h-full w-[161px] bg-white md:block':
                        opened,
                }"
            ></div>
        </transition>

        <div
            class="relative z-20 flex h-full min-h-0 flex-col"
            :class="[
                hasHubspotOverlay ? 'pt-[62px]' : 'pt-[24px]',
                isShortScreen
                    ? 'overflow-y-scroll'
                    : hasHubspotOverlay
                      ? 'overflow-y-auto'
                      : 'overflow-y-hidden',
            ]"
        >
            <nav
                class="flex-1 space-y-[5px] px-[10px] pb-[25px]"
                aria-label="Sidebar"
                role="navigation"
            >
                <div class="relative hidden h-10 md:block xl:hidden">
                    <transition
                        enter-active-class="transition ease-in-out duration-800"
                        enter-from-class="transform scale-0 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-100 ease-in "
                        leave-from-class="opacity-100 "
                        leave-to-class="opacity-0 "
                    >
                        <button
                            v-if="!opened"
                            class="absolute mx-2 mb-[16px] h-6 w-6 text-gray-600 xl:hidden"
                            @click="$emit('open')"
                        >
                            <Bars3BottomLeftIcon />
                        </button>
                    </transition>

                    <transition
                        enter-active-class="transition duration-1000 ease-in-out"
                        enter-from-class="transform rotate-180 opacity-0 scale-80"
                        enter-to-class="transform scale-100 rotate-0 opacity-100"
                        leave-active-class="transition duration-75 ease-in "
                        leave-from-class="opacity-100 "
                        leave-to-class="opacity-0 "
                    >
                        <button
                            v-if="opened"
                            class="absolute mx-2 mb-[16px] h-6 w-6 text-gray-600 xl:hidden"
                            @click="$emit('close')"
                        >
                            <XMarkIcon />
                        </button>
                    </transition>
                </div>

                <template v-for="link in links" :key="link.href">
                    <div
                        v-if="shouldShowToolsSection(link.id)"
                        class="px-4 pb-1 pt-5 text-[11px] font-medium uppercase tracking-wide text-gray-400"
                        :class="opened ? 'block' : 'hidden xl:block'"
                    >
                        {{ t("vue.tools_sidebar_section") }}
                    </div>

                    <div
                        v-if="shouldShowOtherSection(link.id)"
                        class="px-4 pb-1 pt-5 text-[11px] font-medium uppercase tracking-wide text-gray-400"
                        :class="opened ? 'block' : 'hidden xl:block'"
                    >
                        {{ t("vue.other_sidebar_section") }}
                    </div>

                    <inertia-link
                        v-tooltip="{
                            content: link.name,
                            delay: { show: 1250, hide: 150 },
                        }"
                        :href="link.href"
                        :aria-current="page.startsWith(link.id) ? 'page' : null"
                        class="group block w-full"
                        @click="$emit('close')"
                    >
                        <div
                            class="flex h-12 w-full items-center rounded-[6.75px] px-4 py-3 transition-all duration-200"
                            :class="[
                                (opened ? 'gap-7' : 'justify-center') +
                                    ' xl:justify-start xl:gap-7',
                                page.startsWith(link.id)
                                    ? 'bg-gray-200 text-gray-600'
                                    : 'text-gray-500 hover:bg-gray-200 hover:text-gray-600',
                            ]"
                            :data-helper-tooltip="link.name.toLocaleLowerCase()"
                        >
                            <component
                                :is="link.icon"
                                class="h-5 w-5 shrink-0 transition duration-200 group-hover:text-gray-600"
                            />

                            <span
                                class="truncate text-base font-medium"
                                :class="opened ? 'inline' : 'hidden xl:inline'"
                            >
                                {{ link.name }}
                            </span>

                            <div
                                v-if="link.count !== undefined || link.badge"
                                class="ml-auto hidden items-center gap-2"
                                :class="
                                    opened
                                        ? 'inline-flex'
                                        : 'hidden xl:inline-flex'
                                "
                            >
                                <span
                                    v-if="link.count !== undefined"
                                    class="h-5 min-w-[37px] items-center justify-center rounded-2xl bg-gray-100 px-2 text-xs font-normal text-gray-500"
                                >
                                    {{ link.count }}
                                </span>
                                <span
                                    v-if="link.badge"
                                    class="inline-flex h-5 items-center justify-center rounded-2xl bg-[#FF421E] px-2 text-[10px] font-semibold text-white"
                                >
                                    {{ link.badge }}
                                </span>
                            </div>
                        </div>
                    </inertia-link>
                </template>
            </nav>

            <div class="mt-auto pb-2">
                <FreePlanStatusCounter
                    :opened="opened"
                    :has-free-plan="globalProps.hasFreePlan"
                    :free-plan-last-period-number-of-report="
                        globalProps.freePlanLastPeriodNumberOfReport
                    "
                    @upgrade="goToSubscriptions"
                />

                <FreeTrialStatusCounter
                    :opened="opened"
                    :is-trialing="globalProps.isTrialing"
                    :is-trial-expired="globalProps.isTrialExpired"
                    :is-subscribed="globalProps.isSubscribed"
                    :has-free-plan="globalProps.hasFreePlan"
                    :total-days="globalProps.totalDays"
                    :default-trial-days="globalProps.defaultTrialDays"
                    :used-days="globalProps.usedDays"
                    @upgrade="goToSubscriptions"
                />

                <div class="mt-[4px] w-full px-[10px] pb-[7px]">
                    <LanguageSwitcher
                        :compact="!opened && !isXl"
                        :sidebar-right="!opened && !isXl ? 64 : 250"
                    />
                </div>

                <div class="mt-[4px] w-full px-[10px] pb-[7px]">
                    <inertia-link
                        v-tooltip="{
                            content: t('vue.support'),
                            delay: { show: 1250, hide: 150 },
                        }"
                        href="/support"
                        class="group block w-full"
                        @click="$emit('close')"
                    >
                        <div
                            class="w-full rounded-[6.75px] transition-all duration-200 hover:bg-gray-200"
                            data-helper-tooltip="support"
                        >
                            <div
                                class="flex h-12 w-full items-center px-4 py-3 text-base font-medium text-gray-500 transition-all duration-200 hover:text-gray-600"
                                :class="
                                    (!opened ? 'justify-center' : 'gap-7') +
                                    ' xl:justify-start xl:gap-7'
                                "
                            >
                                <svg
                                    class="h-[18px] w-[18px] shrink-0 origin-center scale-110 text-gray-500 transition duration-200 group-hover:text-gray-600"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                >
                                    <g clip-path="url(#clip0_5925_6262)">
                                        <path
                                            d="M14.0392 5.49767H13.9875C13.2188 -1.83522 2.77789 -1.82989 2.01193 5.49767H1.96021C1.13748 5.49767 0.483723 6.1652 0.483723 6.97414V10.2456C0.483723 11.0706 1.15377 11.722 1.96021 11.722H2.86441C3.74543 12.7367 5.45033 12.1082 5.4498 10.7489V6.47084C5.45997 5.20584 3.91707 4.5246 2.99244 5.3686C3.27139 3.97097 4.50757 2.91438 5.986 2.91438H10.0134C11.4918 2.91438 12.728 3.97094 13.0069 5.3686C12.082 4.5243 10.5392 5.20637 10.5496 6.47084V10.7488C10.5393 12.0096 12.0743 12.6932 13.0004 11.856C12.8521 12.535 12.108 14.0536 10.265 14.0536H9.90233C9.70557 13.4692 9.1528 13.047 8.50297 13.047H7.49637C5.5398 13.1215 5.54133 15.9262 7.49637 16H8.50297C9.1528 16 9.70557 15.5777 9.90233 14.9934H10.265C12.2038 14.9934 13.7512 13.5346 13.9767 11.722H14.0392C14.8474 11.722 15.5157 11.0689 15.5157 10.2455V6.97414C15.5157 6.16574 14.8624 5.49767 14.0392 5.49767ZM2.4969 10.7822H1.96021C1.66393 10.7822 1.42348 10.5425 1.42348 10.2455V6.97414C1.42348 6.6773 1.66393 6.43744 1.96021 6.43744H2.4969V10.7822ZM3.43667 6.47084C3.46373 5.75964 4.48327 5.76017 4.5101 6.47084V10.7488C4.48303 11.46 3.4635 11.4595 3.43667 10.7488V6.47084ZM10.0134 1.97462H5.98603C5.49307 1.97462 5.0208 2.06437 4.58447 2.22851C6.40443 0.475709 9.59533 0.476082 11.415 2.22848C10.9787 2.06437 10.5064 1.97462 10.0134 1.97462ZM12.5628 10.7488C12.5357 11.46 11.5162 11.4595 11.4894 10.7488V6.47084C11.5164 5.75964 12.536 5.76017 12.5628 6.47084V10.7488ZM8.503 15.0602H7.4964C6.7852 15.0331 6.7857 14.0136 7.4964 13.9868H8.503C9.21423 14.0139 9.21367 15.0334 8.503 15.0602ZM14.5759 10.2455C14.5759 10.5418 14.3361 10.7822 14.0392 10.7822H13.5025V6.43744H14.0392C14.3348 6.43744 14.5759 6.6767 14.5759 6.97414V10.2455Z"
                                            fill="currentColor"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5925_6262">
                                            <rect
                                                width="16"
                                                height="16"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>

                                <span
                                    class="truncate"
                                    :class="
                                        opened ? 'inline' : 'hidden xl:inline'
                                    "
                                >
                                    {{ t("vue.support") }}
                                </span>
                            </div>
                        </div>
                    </inertia-link>
                </div>

                <div class="mt-[4px] w-full px-[10px] pb-[7px]">
                    <a
                        v-tooltip="{
                            content: t('vue.documentation'),
                            delay: { show: 1250, hide: 150 },
                        }"
                        href="https://docs.dmarceye.com"
                        target="_blank"
                        rel="noopener"
                        class="group block w-full"
                        @click="$emit('close')"
                    >
                        <div
                            class="w-full rounded-[6.75px] transition-all duration-200 hover:bg-gray-200"
                            data-helper-tooltip="documentation"
                        >
                            <div
                                class="flex h-12 w-full items-center px-4 py-3 text-base font-medium text-gray-500 transition-all duration-200 hover:text-gray-600"
                                :class="
                                    (!opened ? 'justify-center' : 'gap-7') +
                                    ' xl:justify-start xl:gap-7'
                                "
                            >
                                <BookOpenIcon
                                    class="h-5 w-5 shrink-0 transition duration-200 group-hover:text-gray-600"
                                />

                                <span
                                    class="truncate"
                                    :class="
                                        opened ? 'inline' : 'hidden xl:inline'
                                    "
                                >
                                    {{ t("vue.documentation") }}
                                </span>

                                <img
                                    :src="newWindowIconUrl"
                                    alt=""
                                    aria-hidden="true"
                                    class="ml-auto h-4 w-4 shrink-0 opacity-60 transition-opacity duration-200 group-hover:opacity-90"
                                    :class="
                                        opened
                                            ? 'inline-block'
                                            : 'hidden xl:inline-block'
                                    "
                                />
                            </div>
                        </div>
                    </a>
                </div>

                <!--TODO: delete sidepanel teams menu later fully as a whole implementation-->

                <div
                    class="w-16 flex-1 space-y-1 px-3 transition-[width] duration-200 w-full xl:px-4"
                >
                    <a
                        v-tooltip="{
                            content: 'Changelog',
                            delay: { show: 1250, hide: 150 },
                        }"
                        href="/changelog"
                        class="group flex items-center justify-between rounded-md px-2 py-2 text-sm font-medium transition-all xl:w-auto"
                        @click="$emit('close')"
                    >
                        <div class="w-full">
                            <div
                                v-if="!opened"
                                class="flex h-10 items-center justify-center xl:hidden"
                            >
                                <LightBulbIcon
                                    class="h-6 w-6 shrink-0 text-gray-500 transition duration-200 group-hover:text-gray-600"
                                />
                            </div>

                            <div
                                v-if="globalProps.latestChangelog"
                                class="border-t border-gray-200 pt-4 space-y-1"
                                :class="opened ? 'block' : 'hidden xl:block'"
                            >
                                <p
                                    class="text-base font-semibold text-gray-600 whitespace-nowrap"
                                >
                                    DMARCeye {{ $t("vue.version") }}
                                    {{ globalProps.latestChangelog.version }}
                                </p>
                                <p
                                    class="text-sm text-gray-400 whitespace-nowrap"
                                >
                                    {{ $t("vue.last_update") }}
                                    {{
                                        globalProps.latestChangelog.release_date
                                    }}
                                </p>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    Bars3BottomLeftIcon,
    XMarkIcon,
    BookOpenIcon,
    LightBulbIcon,
} from "@heroicons/vue/24/outline";
import FreePlanStatusCounter from "@/components/Navigation/FreePlanStatusCounter.vue";
import FreeTrialStatusCounter from "@/components/Navigation/FreeTrialStatusCounter.vue";

import { usePage } from "@inertiajs/vue3";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations.ts";
import { router } from "@inertiajs/vue3";
import newWindowIconUrl from "@/assets/images/new-window.svg?url";
import { useHubspotOverlay } from "@/composables/useHubspotOverlay.ts";

const isXl = ref(false);
const isShortScreen = ref(false);
const { hasHubspotOverlay } = useHubspotOverlay();

const updateIsXl = () => {
    isXl.value = window.matchMedia("(min-width: 1326px)").matches;
};

const updateIsShortScreen = () => {
    isShortScreen.value = window.innerHeight <= 800;
};

const updateScreenState = () => {
    updateIsXl();
    updateIsShortScreen();
};

onMounted(() => {
    updateScreenState();
    window.addEventListener("resize", updateScreenState, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", updateScreenState);
});

const goToSubscriptions = () => {
    router.visit(route("billing.subscriptions.index"));
};

defineProps({
    opened: {
        type: Boolean,
        default: false,
    },
});

defineEmits(["open", "close"]);

const { links } = sidebar();

const page = computed(() => usePage<IRhizomPageProps>().url);
const globalProps = computed(() => usePage<IRhizomPageProps>().props);

const { t } = useTranslations();

const shouldShowToolsSection = (linkId: string): boolean => {
    return linkId === "/domains";
};

const shouldShowOtherSection = (linkId: string): boolean => {
    return linkId === "/dns-tools";
};
</script>

<style></style>
